/**
 * @cfleet/auth - Storage abstraction
 *
 * Provides a platform-agnostic interface for token storage.
 * - Web: Uses localStorage (default)
 * - React Native: Uses expo-secure-store (via native-storage.ts)
 *
 * Usage:
 *   // Web apps use the default storage
 *   import { getTokenStorage } from '@cfleet/auth';
 *
 *   // React Native apps configure secure storage
 *   import { setTokenStorage } from '@cfleet/auth';
 *   import { secureStorage } from '@cfleet/auth/native-storage';
 *   setTokenStorage(secureStorage);
 */

/**
 * Storage interface for platform-agnostic token storage
 */
export interface TokenStorage {
  /**
   * Get an item from storage
   * @param key - The key to retrieve
   * @returns The stored value, or null if not found
   */
  getItem(key: string): Promise<string | null>;

  /**
   * Set an item in storage
   * @param key - The key to store
   * @param value - The value to store
   */
  setItem(key: string, value: string): Promise<void>;

  /**
   * Remove an item from storage
   * @param key - The key to remove
   */
  removeItem(key: string): Promise<void>;
}

/**
 * Storage keys used by the auth package
 */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'cfleet_access_token',
  REFRESH_TOKEN: 'cfleet_refresh_token',
  USER: 'cfleet_user',
  CURRENT_TENANT: 'cfleet_current_tenant',
} as const;

/**
 * Web storage implementation using localStorage
 * This is the default storage used on web platforms
 */
export const webStorage: TokenStorage = {
  async getItem(key: string): Promise<string | null> {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return localStorage.getItem(key);
  },

  async setItem(key: string, value: string): Promise<void> {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    localStorage.setItem(key, value);
  },

  async removeItem(key: string): Promise<void> {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    localStorage.removeItem(key);
  },
};

/**
 * In-memory storage implementation for testing or SSR
 */
export const memoryStorage: TokenStorage = (() => {
  const store = new Map<string, string>();

  return {
    async getItem(key: string): Promise<string | null> {
      return store.get(key) ?? null;
    },

    async setItem(key: string, value: string): Promise<void> {
      store.set(key, value);
    },

    async removeItem(key: string): Promise<void> {
      store.delete(key);
    },
  };
})();

/**
 * Current storage implementation
 * Defaults to web storage, can be changed via setTokenStorage()
 */
let currentStorage: TokenStorage = webStorage;

/**
 * Set the storage implementation to use
 * Call this early in your app's initialization to configure storage
 *
 * @param storage - The storage implementation to use
 *
 * @example
 * // In React Native app entry point
 * import { setTokenStorage } from '@cfleet/auth';
 * import { secureStorage } from '@cfleet/auth/native-storage';
 *
 * setTokenStorage(secureStorage);
 */
export function setTokenStorage(storage: TokenStorage): void {
  currentStorage = storage;
}

/**
 * Get the current storage implementation
 *
 * @returns The current TokenStorage implementation
 */
export function getTokenStorage(): TokenStorage {
  return currentStorage;
}

/**
 * Reset storage to the default web implementation
 * Useful for testing
 */
export function resetTokenStorage(): void {
  currentStorage = webStorage;
}

/**
 * Helper function to get an auth token
 * @param key - The storage key (use AUTH_STORAGE_KEYS)
 */
export async function getAuthItem(key: string): Promise<string | null> {
  return currentStorage.getItem(key);
}

/**
 * Helper function to set an auth token
 * @param key - The storage key (use AUTH_STORAGE_KEYS)
 * @param value - The value to store
 */
export async function setAuthItem(key: string, value: string): Promise<void> {
  return currentStorage.setItem(key, value);
}

/**
 * Helper function to remove an auth token
 * @param key - The storage key (use AUTH_STORAGE_KEYS)
 */
export async function removeAuthItem(key: string): Promise<void> {
  return currentStorage.removeItem(key);
}

/**
 * Clear all auth-related items from storage
 */
export async function clearAuthStorage(): Promise<void> {
  await Promise.all([
    currentStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN),
    currentStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
    currentStorage.removeItem(AUTH_STORAGE_KEYS.USER),
    currentStorage.removeItem(AUTH_STORAGE_KEYS.CURRENT_TENANT),
  ]);
}
